import React, {useState, useMemo} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import Lottie from "lottie-react";
import "../styles/Home.scss";
import {path} from "../router/path";
import {SLIDER_CONFIG} from "../assets/constants/sliderConfig";
import {HOME_ASSETS} from "../assets/images/homeAssets";
import {getArtistFromPath} from "src/utils/artist.utils";
import {useTranslation} from "react-i18next";

const Home = () => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState<number>(0);
  const [openFaqIndex, setOpenFaqIndex] = useState<number>(0);
  const [dragStart, setDragStart] = useState<number>(0);
  const [dragOffset, setDragOffset] = useState<number>(0);
  const [isDragging, setIsDragging] = useState<boolean>(false);

  const location = useLocation();
  const artist = getArtistFromPath(location.pathname);

  // i18n 적용
  const {t} = useTranslation(artist);
  const {t: tCommon} = useTranslation("common");

  const faqData = useMemo(
    () =>
      tCommon("faq", {returnObjects: true}) as Array<{
        question: string;
        answer: string;
      }>,
    [tCommon],
  );

  const noticeData = useMemo(
    () =>
      t("home.section4.notice.items", {
        returnObjects: true,
      }) as Array<{
        label: string;
        content: string;
      }>,
    [t, artist],
  );

  // Section 2 슬라이더 데이터: i18n과 Assets 이미지 배열 결합
  const sliderItems = useMemo(
    () =>
      t("home.section2.items", {
        returnObjects: true,
      }) as Array<{
        title: string;
        description: string;
      }>,
    [t, artist],
  );

  const sliderImages = HOME_ASSETS[artist].section2.slides;
  const sliderData = useMemo(
    () =>
      sliderItems.map((item, index) => ({
        ...item,
        image: sliderImages[index],
      })),
    [sliderItems, sliderImages],
  );

  const handleDotClick = (index: number) => {
    setCurrentSlide(index);
  };

  const handleDragStart = (
    e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>,
  ) => {
    const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
    setDragStart(clientX);
    setIsDragging(true);
    setDragOffset(0);
  };

  const handleDragMove = (
    e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>,
  ) => {
    if (!isDragging) return;
    const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
    const diff = clientX - dragStart;
    setDragOffset(diff);
  };

  const handleDragEnd = () => {
    if (!isDragging) return;

    if (Math.abs(dragOffset) > SLIDER_CONFIG.DRAG_THRESHOLD) {
      if (dragOffset > 0 && currentSlide > 0) {
        setCurrentSlide(currentSlide - 1);
      }
      if (dragOffset < 0 && currentSlide < sliderData.length - 1) {
        setCurrentSlide(currentSlide + 1);
      }
    }

    setIsDragging(false);
    setDragOffset(0);
  };

  const handleFaqClick = (index: number) => {
    setOpenFaqIndex(index === openFaqIndex ? -1 : index);
  };
  return (
    <div className="home-container">
      <section className="home-section section-1">
        <div className="section-1-container">
          <img
            src={HOME_ASSETS[artist].section1.bg}
            alt="Section 1 Background"
            className="section-1-bg"
          />
          <div className="section-1-content">
            <div className="section-1-title-wrapper">
              <img
                src={HOME_ASSETS[artist].section1.title}
                alt="Section 1 Title"
                className="section-1-title"
              />
              <div className={`section-1-title-text ${artist}`}>
                {t("home.section1.title")}
              </div>
            </div>
            <Lottie
              animationData={HOME_ASSETS[artist].section1.lottie}
              className="section-1-lottie"
            />
            <button
              className={`section-1-button ${artist}`}
              onClick={() => navigate(path.waiting(artist))}
            >
              {tCommon("button.start")}
            </button>
          </div>
        </div>
      </section>
      <section className="home-section section-2">
        <div className="section-2-container">
          <div className={`section-2-banner ${artist}`}>
            <span className="banner-text">{t("home.section2.banner")}</span>
          </div>
          <h2 className={`section-2-title ${artist}`}>
            {t("home.section2.title")}
          </h2>
          <p className={`section-2-subtitle ${artist}`}>
            {t("home.section2.subtitle")}
          </p>
          <div
            className="section-2-slider"
            onMouseDown={handleDragStart}
            onMouseMove={handleDragMove}
            onMouseUp={handleDragEnd}
            onMouseLeave={handleDragEnd}
            onTouchStart={handleDragStart}
            onTouchMove={handleDragMove}
            onTouchEnd={handleDragEnd}
            style={{
              cursor: isDragging ? "grabbing" : "grab",
              userSelect: "none",
            }}
          >
            <div
              className="slider-wrapper"
              style={{
                transform: `translateX(calc(-${currentSlide} * 100% / ${SLIDER_CONFIG.SLIDE_COUNT} + ${dragOffset}px))`,
                transition: isDragging
                  ? "none"
                  : `transform ${SLIDER_CONFIG.TRANSITION_DURATION}ms ease-in-out`,
              }}
            >
              {sliderData.map((item, index) => (
                <div key={index} className="slide">
                  <div className="section2-slide-container">
                    <img src={item.image} alt={item.title} draggable={false} />
                    <div className={`section2-slide-content ${artist}`}>
                      <h3 className={`section2-slide-title ${artist}`}>
                        {item.title}
                      </h3>
                      <p className={`section2-slide-description ${artist}`}>
                        {item.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="pagination-dots">
              {sliderData.map((_, index) => (
                <button
                  key={index}
                  className={`dot ${artist} ${index === currentSlide ? "active" : ""}`}
                  onClick={() => handleDotClick(index)}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>
      <section className={`home-section section-3 ${artist}`}>
        <div className="section-3-container">
          <img
            src={HOME_ASSETS[artist].section3.bg}
            alt="Section 3 Background"
            className="section-3-bg"
          />
          <div className="section-3-content">
            <h2 className={`section-3-title ${artist}`}>
              {t("home.section3.title")}
            </h2>
            <h3 className={`section-3-subtitle ${artist}`}>
              {t("home.section3.subtitle")}
            </h3>
            <div className="section-3-cards-wrapper">
              <img
                src={HOME_ASSETS[artist].section3.content}
                alt="Section 3 Content"
                className="section-3-cards"
              />
            </div>
          </div>
        </div>
      </section>
      <section className={`home-section section-4 ${artist}`}>
        <div className="section-4-container">
          <div className={`section-4-header ${artist}`}>
            <h2 className={`section-4-header-title ${artist}`}>
              {t("home.section4.title")}
            </h2>
            <p className={`section-4-header-subtitle ${artist}`}>
              {t("home.section4.subtitle")}
            </p>
          </div>
          <div className={`section-4-content ${artist}`}>
            <img
              src={HOME_ASSETS[artist].section4.backgroundStar}
              alt="star"
              className="section-4-background-star first"
            />
            <img
              src={HOME_ASSETS[artist].section4.backgroundStar}
              alt="star"
              className="section-4-background-star second"
            />
            <img
              src={HOME_ASSETS[artist].section4.backgroundStar}
              alt="star"
              className="section-4-background-star third"
            />
            <div className="section-4-step">
              <div className="section-4-step-header">
                <div className={`section-4-step-badge ${artist}`}>01</div>
                <h3 className={`section-4-step-title ${artist}`}>
                  {t("home.section4.step1.title")}
                </h3>
              </div>
              <span className={`section-4-step-description ${artist}`}>
                {t("home.section4.step1.description")}
              </span>
              <div className="section-4-step-content">
                <img
                  src={HOME_ASSETS[artist].section4.steps[0]}
                  alt="Step 1"
                  className="section-4-step-image"
                />
              </div>
            </div>

            <div className="section-4-step">
              <div className="section-4-step-header">
                <div className={`section-4-step-badge ${artist}`}>02</div>
                <h3 className={`section-4-step-title ${artist}`}>
                  {t("home.section4.step2.title")}
                </h3>
              </div>
              <span className={`section-4-step-description ${artist}`}>
                {t("home.section4.step2.description")}
              </span>
              <div className="section-4-step-content">
                <img
                  src={HOME_ASSETS[artist].section4.steps[1]}
                  alt="Step 2"
                  className="section-4-step-image"
                />
              </div>
            </div>

            <div className="section-4-step">
              <div className="section-4-step-header">
                <div className={`section-4-step-badge ${artist}`}>03</div>
                <h3 className={`section-4-step-title ${artist}`}>
                  {t("home.section4.step3.title")}
                </h3>
              </div>
              <span className={`section-4-step-description ${artist}`}>
                {t("home.section4.step3.description")}
              </span>
              <div className="section-4-step-content">
                <img
                  src={HOME_ASSETS[artist].section4.steps[2]}
                  alt="Step 3"
                  className="section-4-step-image"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="section-4-notice">
          <h3 className={`section-4-notice-title ${artist}`}>
            {t("home.section4.notice.title")}
          </h3>
          <ul className={`section-4-notice-list ${artist}`}>
            {noticeData.map((notice, index) => (
              <li key={index}>
                <span className="section-4-notice-list-title">
                  {notice.label}
                </span>
                {" : "}
                <span className="section-4-notice-list-content">
                  {notice.content}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </section>
      <section className={`home-section section-5 ${artist}`}>
        <div className="section-5-container">
          <h2 className={`section-5-title ${artist}`}>
            {t("home.section5.title")}
          </h2>
          <p className={`section-5-subtitle ${artist}`}>
            {t("home.section5.subtitle")}
          </p>
          <div className="faq-list">
            {faqData.map((faq, index) => (
              <div key={index} className={`faq-item ${artist}`}>
                <button
                  className={`faq-question ${artist}`}
                  onClick={() => handleFaqClick(index)}
                >
                  <span>{faq.question}</span>
                  <img
                    src={HOME_ASSETS[artist].arrow}
                    alt="arrow"
                    className={`faq-icon ${openFaqIndex === index ? "open" : ""}`}
                  />
                </button>
                {openFaqIndex === index && (
                  <div className={`faq-answer ${artist}`}>{faq.answer}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className={`home-section section-6 ${artist}`}>
        <div className="section-6-container">
          <div className={`section-6-content ${artist}`}>
            <div className="section-6-wrapper">
              <div className={`section-6-label ${artist}`}>
                {t("home.section6.label")}
              </div>
              <button
                className={`section-6-button ${artist}`}
                onClick={() => navigate(path.waiting(artist))}
              >
                {tCommon("button.start")}
              </button>
            </div>
          </div>
        </div>
      </section>
      <section className={`home-section section-7 ${artist}`}>
        <img
          src={HOME_ASSETS[artist].section6.image}
          alt="Section 7"
          className={`section-7-image ${artist}`}
        />
      </section>
    </div>
  );
};

export default Home;
